"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateCaseSchema = exports.CaseIMSchema = exports.CaseSchema = exports.BaseCaseSchema = exports.IPProtectionStateSchema = exports.IPProtectionActionsSchema = exports.CommercialisationStateSchema = exports.CommercialisationActionsSchema = void 0;
const zod_1 = require("zod");
const disclosure_1 = require("./disclosure");
const license_schema_1 = require("./license-schema");
const patent_schema_1 = require("./patent-schema");
const case_enums_1 = require("../enums/case-enums");
/* These are the ACTIONS needing to be completed on the COMMERCIALISATION SWIMLANE.
 * Visual checkboxes will be ticked off based on the status of every action.
 * Certain actions completed will result in displaying NEW TASKS to complete as well as STATE CHANGES.
 * Relevant descriptions for ACTION AVAILIBILTY and STATE CHANGES are provided below:
    ******************************************* GATE_C1 ********************************************************************
    hasUploadedPresentation: available once 'hasCompletedPriorArtSearch' is completed
    hasCompletedHypothesis:  available once'hasCompletedPriorArtSearch' is completed
    gateC1HasPresentedToCommittee: available once 'gateC1HasUploadedPresentation' AND 'gateC1HasCompletedHypothesis' are completed
    gateC1DecisionRecorded:  available once 'hasPresentedToCommittee' is completed
                             STATE CHANGE from GATE_C1 once complete
    ******************************************* GATE_SO1 *******************************************************************
    gateSO1HasBeenCompleted:     available after GATE_C1 state has transitioned to GATE_SO1
                             STATE CHANGE from GATE_SO1 TO SPIN_OUT
    ******************************************* LICENSE_TRACK **************************************************************
    Note: This state is more of a management system  in which licenses can be added, managed and deleted

    licenseTrackHasUploadedMarketingMaterial: available after GATE_C1 state has transitioned to LICENSE_TRACK
    licenseTrackPriorityDateSecured:          NOTE: This is the only action that is coupled to the IP PROTECTION SWIMLANE
                                              This action is auto-set during the IP PROTECTION - GATE_P2 State
    After both of the actions above have been completed then we can fully show the LICENSE MANAGEMENT SYSTEM
----------------------------------------------------------------------------------------------------------------------------------
    Note: The following actions are to be stored in the License Object as these are replicable actions needed for EVERY LICENSE

    hasCompletedDueDiligence:     available once 'hasUploadedMarketingMaterial' AND 'priorityDateSecured' are completed
    hasUploadedLicenseAgreement:  NOTE: This action is available not only after 'hasCompletedDueDiligence' is completed
                                  but also based off of the Due Diligence decision. If the decision is FAILED or of a
                                  similiar result then the license track for this licensee ends here.

 ******************************************* NO_COMMERCIALISATION **************************************************************
 noCommercialisationHasNotifiedSubmitter: available after transitioning to NO_COMMERCIALISATION from GATE_C1
                    
 */
exports.CommercialisationActionsSchema = zod_1.z.object({
    gateC1HasCompletedPriorArtSearch: zod_1.z.boolean().default(false),
    gateC1HasUploadedPresentation: zod_1.z.boolean().default(false),
    gateC1HasCompletedHypothesis: zod_1.z.boolean().default(false),
    gateC1HasPresentedToCommittee: zod_1.z.boolean().default(false),
    gateC1DecisionRecorded: zod_1.z.boolean().default(false),
    gateSO1HasBeenCompleted: zod_1.z.boolean().default(false),
    licenseTrackHasUploadedMarketingMaterial: zod_1.z.boolean().default(false),
    licenseTrackPriorityDateSecured: zod_1.z.boolean().default(false),
    noCommercialisationHasNotifiedSubmitter: zod_1.z.boolean().default(false),
});
/* These are the possible STATES that a case could be in on the COMMERCIALISATION SWIMLANE.
 * Relevant descriptions for where a state would typically change to is provided below:

    GATE_C1  - CAN CHANGE TO THE FOLLOWING: GATE_SO1 , LICENSE_TRACK , NO_COMMERCIALISATION
    GATE_SO1 - CAN CHANGE TO THE FOLLOWING: SPIN_OUT
 */
exports.CommercialisationStateSchema = zod_1.z.nativeEnum(case_enums_1.CommercialisationState);
/* These are the ACTIONS needing to be completed on the IP PROTECTION SWIMLANE.
 * Visual checkboxes will be ticked off based on the status of every action.
 * Certain actions completed will result in displaying NEW TASKS to complete as well as STATE CHANGES.
 *  Relevant descriptions for ACTION AVAILIBILTY and STATE CHANGES are provided below:
    
    ****************************************** GATE_P1 **********************************************
    gateP1AssessmentFormCompleted: allows 'gateP1DecisionRecorded' to be completed
    gateP1DecisionRecorded: STATE CHANGE to IP_ABANDONED or IP_PRIORITY once completed
    **************************************** IP_PRIORITY ********************************************
    ipPriorityHasAppliedForIPProtection: STATE CHANGE to GATE_P2 once complete
    ***************************************** GATE_P2 ***********************************************
     gateP2AssessmentFormCompleted: allows 'gateP2PursuePatentDecisionRecorded' to be completed
    gateP2PursuePatentDecisionRecorded: STATE CHANGE to NIPMO or GATE_P3 once completed
    ***************************************** GATE_P3 ***********************************************
    gateP3AssessmentFormCompleted: allows 'gateP3HasFiledForeignPatentStrategy' to be completed
    gateP3HasFiledForeignPatentStrategy: STATE CHANGE to NIPMO or NATIONAL once completed
    ****************************************** NIPMO ************************************************
    nipmoOfferToNipmoDecisionRecorded: STATE CHANGE to INVENTORS or ABANDONED once completed
    **************************************** INVENTORS **********************************************
    inventorsOfferToInventorDecisionRecorded: STATE CHANGE to ? or ABANDONED once completed
    **************************************** NATIONAL ***********************************************
    Note: This state is more of a management system in which patents can be added, managed and deleted

    Typical Features: - Add new territories patents to list
                      - Set renewal schedule
                      - Get reminders for maintenance and renewal
                      - Abandon patents (& Cancel Abandonments)
                      - 6-month auto lapses patent state to NIPMO
 */
exports.IPProtectionActionsSchema = zod_1.z.object({
    gateP1AssessmentFormCompleted: zod_1.z.boolean().default(false), // Allowing 'gateP1DecisionRecorded' to be completed on the platform rather than Jotform
    gateP1DecisionRecorded: zod_1.z.boolean().default(false),
    ipPriorityHasAppliedForIPProtection: zod_1.z.boolean().default(false),
    gateP2AssessmentFormCompleted: zod_1.z.boolean().default(false), // Allowing 'gateP2PursuePatentDecisionRecorded' to be completed on the platform rather than Jotform
    gateP2PursuePatentDecisionRecorded: zod_1.z.boolean().default(false),
    gateP3AssessmentFormCompleted: zod_1.z.boolean().default(false), // Allowing 'gateP3HasFiledForeignPatentStrategy' to be completed on the platform rather than Jotform
    gateP3HasFiledForeignPatentStrategy: zod_1.z.boolean().default(false),
    nipmoOfferToNipmoDecisionRecorded: zod_1.z.boolean().default(false),
    inventorsOfferToInventorDecisionRecorded: zod_1.z.boolean().default(false),
});
/* These are the possible STATES that a case could be in on the IP PROTECTION SWIMLANE.
 * Relevant descriptions for where a state would typically change to is provided below.

    IP-INACTIVE - The default/initial state of the IP PROTECTION TRACK.
                  CAN CHANGE TO THE FOLLOWING: GATE_P1
            NOTE: This is the only coupled state to the COMMERCIALISATION SWIMLANE.
                  State Transition to GATE_P1 occurs once COMMERCIALISATION SWIMLINE GATE_C1
                  has been completed and has transitioned to either GATE_SO1 or LICENSE_TRACK.

    GATE_P1     - CAN CHANGE TO THE FOLLOWING: IP_ABANDONED , IP_PRIORITY
    IP_PRIORITY - CAN CHANGE TO THE FOLLOWING: GATE_P2
    GATE_P2     - CAN CHANGE TO THE FOLLOWING: NIPMO , GATE_P3
    GATE_P3     - CAN CHANGE TO THE FOLLOWING: NIPMO, NATIONAL
    NATIONAL    - CAN CHANGE TO THE FOLLOWING: NIPMO, ABANDONED
    NIPMO       - CAN CHANGE TO THE FOLLOWING: INVENTORS, ABANDONED
    INVENTORS   - CAN CHANGE TO THE FOLLOWING:  ? , ABANDONED
    
 */
exports.IPProtectionStateSchema = zod_1.z.nativeEnum(case_enums_1.IPProtectionState);
exports.BaseCaseSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    primaryFirstName: zod_1.z.string(),
    primaryLastName: zod_1.z.string(),
    commerceState: exports.CommercialisationStateSchema,
    commerceActions: exports.CommercialisationActionsSchema,
    ipState: exports.IPProtectionStateSchema,
    ipActions: exports.IPProtectionActionsSchema,
    disclosureId: zod_1.z.string(),
    createdAt: zod_1.z.date(),
    updatedAt: zod_1.z.date(),
});
exports.CaseSchema = zod_1.z.lazy(() => exports.BaseCaseSchema.extend({
    patents: zod_1.z.array(zod_1.z.lazy(() => patent_schema_1.BasePatentSchema)),
    licenses: zod_1.z.array(zod_1.z.lazy(() => license_schema_1.BaseLicenseSchema)),
    disclosure: zod_1.z.lazy(() => disclosure_1.DisclosureSchema),
    // TODO: Uncomment when ready to merge and push changes to the Db.
    //  caseAssessmentForm: z.array(z.lazy(() => BaseCaseAssessmentFormSchema)),
}));
// Note: This is what is ingested into the Db
exports.CaseIMSchema = exports.BaseCaseSchema.omit({
    id: true,
    updatedAt: true,
    createdAt: true,
});
/* Note: This is the basic Case template received from the FE/User to create a Case
 *       The rest of the Case is initialized and populated with default values
 */
exports.CreateCaseSchema = exports.CaseIMSchema.omit({
    commerceState: true,
    commerceActions: true,
    ipState: true,
    ipActions: true,
});
